import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { LeaderboardEntry, TeamLeaderboardEntry } from '../types/api';

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState<'players' | 'teams'>('players');
  const [playerLeaderboard, setPlayerLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState<TeamLeaderboardEntry[]>([]);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const [playersRes, teamsRes] = await Promise.all([
          api.get('/leaderboard'),
          api.get('/leaderboard/teams')
        ]);
        setPlayerLeaderboard(playersRes.data);
        setTeamLeaderboard(teamsRes.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchLeaderboards();
  }, []);

  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return 'from-yellow-400 to-yellow-600';
      case 2:
        return 'from-gray-300 to-gray-500';
      case 3:
        return 'from-orange-400 to-orange-600';
      default:
        return 'from-primary-500 to-secondary-500';
    }
  };

  const currentLeaderboard = activeTab === 'players' ? playerLeaderboard : teamLeaderboard;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Žebříček
          </h2>
          <p className="text-gray-600 text-lg">Nejlepší {activeTab === 'players' ? 'hráči' : 'týmy'}</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="bg-white/50 backdrop-blur-sm p-1 rounded-2xl flex space-x-2">
            <button
              onClick={() => setActiveTab('players')}
              className={`px-6 py-2 rounded-xl font-bold transition-all duration-200 ${activeTab === 'players'
                  ? 'bg-white shadow-md text-primary-600'
                  : 'text-gray-500 hover:bg-white/50'
                }`}
            >
              Hráči
            </button>
            <button
              onClick={() => setActiveTab('teams')}
              className={`px-6 py-2 rounded-xl font-bold transition-all duration-200 ${activeTab === 'teams'
                  ? 'bg-white shadow-md text-primary-600'
                  : 'text-gray-500 hover:bg-white/50'
                }`}
            >
              Týmy
            </button>
          </div>
        </div>

        {currentLeaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-12">
            <div className="flex flex-col items-center pt-8 sm:pt-12">
              <div className="text-2xl sm:text-3xl mb-2 text-gray-400 font-bold">#2</div>
              <div className="bg-gradient-to-br from-gray-300 to-gray-500 text-white p-4 sm:p-6 rounded-3xl shadow-card w-full text-center transition-all duration-200">
                <div className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">{currentLeaderboard[1]?.name}</div>
                <div className="text-2xl sm:text-4xl font-bold">{currentLeaderboard[1]?.wins}</div>
                <div className="text-xs sm:text-sm opacity-80 mt-1">výhry</div>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="text-3xl sm:text-4xl mb-2 text-yellow-500 font-bold">#1</div>
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 text-white p-4 sm:p-8 rounded-3xl shadow-card w-full text-center transition-all duration-200">
                <div className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2">{currentLeaderboard[0]?.name}</div>
                <div className="text-3xl sm:text-5xl font-bold">{currentLeaderboard[0]?.wins}</div>
                <div className="text-xs sm:text-sm opacity-80 mt-1 sm:mt-2">výhry</div>
              </div>
            </div>

            <div className="flex flex-col items-center pt-16 sm:pt-20">
              <div className="text-2xl sm:text-3xl mb-2 text-orange-400 font-bold">#3</div>
              <div className="bg-gradient-to-br from-orange-400 to-orange-600 text-white p-4 sm:p-6 rounded-3xl shadow-card w-full text-center transition-all duration-200">
                <div className="text-base sm:text-xl font-bold mb-1 sm:mb-2">{currentLeaderboard[2]?.name}</div>
                <div className="text-xl sm:text-3xl font-bold">{currentLeaderboard[2]?.wins}</div>
                <div className="text-xs opacity-80 mt-1">výhry</div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-card border border-white overflow-hidden">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-4 sm:p-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white text-center">Všechna pořadí</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-primary-100 to-secondary-100">
                <tr>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-left text-xs sm:text-sm font-bold text-gray-700">Pořadí</th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-left text-xs sm:text-sm font-bold text-gray-700">{activeTab === 'players' ? 'Hráč' : 'Tým'}</th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-xs sm:text-sm font-bold text-gray-700">Výhry</th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-xs sm:text-sm font-bold text-gray-700">Prohry</th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-xs sm:text-sm font-bold text-gray-700">Win rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentLeaderboard.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-all duration-200"
                  >
                    <td className="py-3 sm:py-4 px-3 sm:px-6">
                      <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r ${getRankStyle(index + 1)} text-white font-bold text-xs sm:text-sm`}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="py-3 sm:py-4 px-3 sm:px-6">
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">{item.name}</span>
                    </td>
                    <td className="py-3 sm:py-4 px-3 sm:px-6 text-center">
                      <span className="inline-flex items-center bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-xl font-bold text-sm sm:text-base">
                        {item.wins}
                      </span>
                    </td>
                    <td className="py-3 sm:py-4 px-3 sm:px-6 text-center">
                      <span className="inline-flex items-center bg-gray-100 text-gray-800 px-3 sm:px-4 py-1 sm:py-2 rounded-xl font-semibold text-sm sm:text-base">
                        {item.losses ?? 0}
                      </span>
                    </td>
                    <td className="py-3 sm:py-4 px-3 sm:px-6 text-center">
                      <span className="font-bold text-gray-800 text-sm sm:text-base">
                        {item.winRate ? `${item.winRate}%` : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {currentLeaderboard.length === 0 && (
          <div className="bg-white/80 backdrop-blur-lg p-8 sm:p-16 rounded-3xl text-center border border-white shadow-card">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">Zatím žádné pořadí</h3>
            <p className="text-gray-600 text-base sm:text-lg">Buďte první, kdo nastoupí a zabere první příčku!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
